import * as Y from 'yjs';
import { composeProjectedScene } from '../../../core/canvas/composeProjectedScene';
import { extractNodesAndEdgesFromDoc } from '../../../utils/snapshotToScene';
import { PROJECTED_LAYOUT_MAP, readProjectedLayout, writeProjectedPosition } from '../../../core/canvas/projectedLayout';

import type { Edge, Node, Scene } from '../../../core/models/scene';
import type { ViewDefinition } from '../../../core/repository/DiagramRepository';
import {
  orgPersonNodeTypeFromCardVariant,
  resolveOrgPersonCardVariant,
} from '../models/defaults';
import { resolveOrgCardAccent } from '../assistant/atlasManualDisplayTypes';
import type { OrgModelProjection, OrgModelReadPrincipal } from '../models/OrgModelProjection';
import { resolvePositionChartGroupingPolicy } from '../models/PositionChartGrouping';
import {
  buildPeopleChartScene,
  type PeopleChartPresentation,
} from './buildPeopleChartScene';
import { buildPositionChartScene } from './buildPositionChartScene';
import { orgProjectionCompletenessError } from './orgProjectionCompleteness';
import { isAtlasFolderProjection } from './atlasProjectionContext';
import { isSavedPeopleView } from '../models/orgArtifactKind';
import { resolveAtlasSavedViewMatches } from './AtlasSavedViewMatching';
import type { AtlasSavedViewDefinition } from '../models/AtlasSavedView';
import { stripBlockedDisplayFields, type ViewRule } from '../../../core/assistant/models/ViewRules';
import { resolveBlockedLensDisplayFields } from './orgAnalyticsLensFields';
import {
  CHAIN_BACKGROUND_COLOR,
  CHAIN_BORDER_COLOR,
  isReportingChainContextNode,
} from './orgChartReportingChainDim';

type OrgPresentationSettings = {
  orgchart?: {
    viewMode?: 'compact' | 'full';
    personCardVariant?: 'photo' | 'text' | 'card';
    cardAccent?: string | null;
    nodeSpacing?: number;
    verticalSpacing?: number;
  };
};

const ORG_EXPORT_VIEW_RULE_ID_KEYS = [
  'precomputedMatchingNodeIds',
  'precomputedChainNodeIds',
] as const;

const copyOrgViewRulesForExport = (
  sourceDoc: Y.Doc,
  exportDoc: Y.Doc,
  scene: Scene,
): void => {
  const source = sourceDoc.getMap<unknown>('viewRules');
  const target = exportDoc.getMap<unknown>('viewRules');
  const rules = source.get('rules');
  if (typeof rules === 'string') target.set('rules', rules);

  const allowedNodeIds = new Set(scene.nodes.map((node) => node.id));
  const allowedEdgeIds = new Set(scene.edges.map((edge) => edge.id));
  for (const key of ORG_EXPORT_VIEW_RULE_ID_KEYS) {
    copyFilteredIdList(source, target, key, allowedNodeIds);
  }
  copyFilteredIdList(source, target, 'precomputedChainEdgeIds', allowedEdgeIds);
};

/**
 * Saved-view rules and transient URL-backed Insight rules can live outside the
 * diagram Y.Doc, so the viewer's lens travels with the export request instead.
 * Client-sent rules only style an already-authorized scene; eligibility is
 * re-applied here from the permissioned model rather than trusted from the
 * client.
 */
const writeViewerRulesForExport = (
  exportDoc: Y.Doc,
  rules: readonly ViewRule[],
  model: OrgModelProjection,
): void => {
  const blocked = resolveBlockedLensDisplayFields(model.lenses?.analytics);
  const allowed = stripBlockedDisplayFields([...rules], blocked);
  exportDoc.getMap<unknown>('viewRules').set('rules', JSON.stringify(allowed));
};

const copyFilteredIdList = (
  source: Y.Map<unknown>,
  target: Y.Map<unknown>,
  key: string,
  allowedIds: ReadonlySet<string>,
): void => {
  const raw = source.get(key);
  if (typeof raw !== 'string' || raw.trim().length === 0) return;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return;
    const filtered = parsed.filter(
      (id): id is string => typeof id === 'string' && allowedIds.has(id),
    );
    target.set(key, JSON.stringify(filtered));
  } catch {
    // Invalid cached ids are ignored; the export assembler can recompute matches.
  }
};
/**
 * Live Atlas projections persist presentation settings only. Export must
 * materialize a temporary scene from the permissioned org model into an
 * isolated Y.Doc — never into the source diagram snapshot.
 */
export const isOrgFolderProjectionExport = isAtlasFolderProjection;

export const readPeopleChartPresentationFromDoc = (
  doc: Y.Doc,
): PeopleChartPresentation => {
  try {
    const raw = doc.getMap('diagramSettings').get('settings');
    if (typeof raw !== 'string' || raw.trim().length === 0) {
      return {
        personNodeType: orgPersonNodeTypeFromCardVariant(resolveOrgPersonCardVariant(undefined)),
        viewMode: undefined,
        cardAccent: resolveOrgCardAccent(undefined),
      };
    }
    const settings = JSON.parse(raw) as OrgPresentationSettings;
    const variant = resolveOrgPersonCardVariant(settings.orgchart?.personCardVariant);
    return {
      personNodeType: orgPersonNodeTypeFromCardVariant(variant),
      viewMode: settings.orgchart?.viewMode,
      cardAccent: resolveOrgCardAccent(settings.orgchart?.cardAccent),
      nodeSpacing: resolveFiniteSpacing(settings.orgchart?.nodeSpacing),
      verticalSpacing: resolveFiniteSpacing(settings.orgchart?.verticalSpacing),
    };
  } catch {
    return {
      personNodeType: orgPersonNodeTypeFromCardVariant(resolveOrgPersonCardVariant(undefined)),
      viewMode: undefined,
      cardAccent: resolveOrgCardAccent(undefined),
    };
  }
};

const resolveFiniteSpacing = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

export class AtlasSavedViewExportUnavailableError extends Error {
  constructor() {
    super('Saved Atlas view is unavailable for export');
  }
}

export type ResolveAtlasSavedViewForExport = (input: {
  readonly folderId: string;
  readonly savedViewId: string;
  readonly principal: OrgModelReadPrincipal;
}) => Promise<AtlasSavedViewDefinition>;

/**
 * Augments a saved view's viewDefinition with its authorized matching-person
 * IDs before the scene is materialized. Fails closed: a missing resolver, or
 * an unauthorized/unavailable definition, throws rather than silently
 * exporting the unfiltered Full Org Chart or an empty-but-valid chart.
 */
export async function resolveExportViewDefinition(
  viewDefinition: ViewDefinition | null | undefined,
  model: OrgModelProjection,
  diagramFolderId: string,
  principal: OrgModelReadPrincipal,
  resolveAtlasSavedView: ResolveAtlasSavedViewForExport | undefined,
): Promise<ViewDefinition | null | undefined> {
  const savedViewId = isSavedPeopleView(viewDefinition) ? viewDefinition?.savedViewId : undefined;
  if (!savedViewId) return viewDefinition;
  if (!resolveAtlasSavedView) throw new AtlasSavedViewExportUnavailableError();
  const definition = await resolveAtlasSavedView({ folderId: diagramFolderId, savedViewId, principal });
  const matchingPersonIds = resolveAtlasSavedViewMatches(model, definition);
  return { ...viewDefinition, matchingPersonIds } as ViewDefinition;
}

export const buildOrgProjectionExportScene = (
  model: OrgModelProjection,
  viewDefinition: ViewDefinition | null | undefined,
  presentationDoc: Y.Doc,
): Scene => {
  if (viewDefinition?.projection === 'planning_chart' || viewDefinition?.type === 'planning-chart') {
    throw new Error('Planning export requires a permissioned scenario revision; a live graph or saved snapshot cannot substitute');
  }
  const completenessError = orgProjectionCompletenessError(model);
  if (completenessError) throw completenessError;
  if (viewDefinition?.projection === 'position_chart' || viewDefinition?.type === 'position-chart') {
    return composeProjectedScene(buildPositionChartScene(model, undefined, {
      groupingPolicy: resolvePositionChartGroupingPolicy(viewDefinition?.filters ?? null),
    }), extractNodesAndEdgesFromDoc(presentationDoc));
  }

  return composeProjectedScene(buildPeopleChartScene(
    model,
    viewDefinition,
    undefined,
    readPeopleChartPresentationFromDoc(presentationDoc),
  ), extractNodesAndEdgesFromDoc(presentationDoc));
};

export const writeOrgSceneIntoExportDoc = (doc: Y.Doc, scene: Scene): void => {
  const shapes = doc.getMap<Record<string, unknown>>('shapes');
  const connectors = doc.getMap<Record<string, unknown>>('connectors');

  doc.transact(() => {
    const existingShapeIds: string[] = [];
    shapes.forEach((_value, id) => existingShapeIds.push(id));
    for (const id of existingShapeIds) shapes.delete(id);

    const existingConnectorIds: string[] = [];
    connectors.forEach((_value, id) => existingConnectorIds.push(id));
    for (const id of existingConnectorIds) connectors.delete(id);

    for (const node of scene.nodes) {
      shapes.set(node.id, shapeRecordFromNode(node));
    }
    for (const edge of scene.edges) {
      connectors.set(edge.id, connectorRecordFromEdge(edge));
    }
  }, 'org-projection-export-materialize');
};

export const materializeOrgProjectionExportDoc = (
  presentationDoc: Y.Doc,
  model: OrgModelProjection,
  viewDefinition: ViewDefinition | null | undefined,
  viewerRules?: readonly ViewRule[],
): Y.Doc => {
  const scene = buildOrgProjectionExportScene(model, viewDefinition, presentationDoc);
  const exportDoc = new Y.Doc();
  // Do not clone unknown maps, fork bases, CSV caches or CRDT tombstones:
  // downstream renderers must see only this caller's materialized graph.
  const settings = presentationDoc.getMap('diagramSettings').get('settings');
  if (typeof settings === 'string') exportDoc.getMap('diagramSettings').set('settings', settings);
  if (viewerRules && viewerRules.length > 0) {
    writeViewerRulesForExport(exportDoc, viewerRules, model);
  } else if (!isSavedPeopleView(viewDefinition)) {
    copyOrgViewRulesForExport(presentationDoc, exportDoc, scene);
  }
  const layout = readProjectedLayout(presentationDoc);
  if (layout) {
    exportDoc.getMap(PROJECTED_LAYOUT_MAP).set('mode', layout.mode);
    // Preserve only authorized placement keys so export can distinguish saved
    // cards from newly revealed descendants without leaking withheld identities.
    for (const node of scene.nodes) {
      const position = layout.positions.get(node.id);
      if (position) writeProjectedPosition(exportDoc, node.id, position);
    }
  }
  writeOrgSceneIntoExportDoc(exportDoc, scene);
  return exportDoc;
};

const shapeRecordFromNode = (node: Node): Record<string, unknown> => {
  const attrs = { ...(node.attrs ?? {}) };
  const props = { ...(node.props ?? {}) };
  const width = typeof node.width === 'number' ? node.width : node.w;
  const height = typeof node.height === 'number' ? node.height : node.h;
  const name =
    (typeof node.name === 'string' ? node.name : undefined)
    ?? (typeof attrs.name === 'string' ? attrs.name : undefined);
  const role =
    (typeof attrs.role === 'string' ? attrs.role : undefined)
    ?? (typeof props.role === 'string' ? props.role : undefined);
  // Match canvas OrgPersonNode / card-accent apply: accent is outline + accent bar,
  // never the card body fill. Collapsed-dot export snapshots also read fillColor
  // separately from stroke/accent.
  const fillColor =
    (typeof node.fillColor === 'string' ? node.fillColor : undefined)
    ?? (typeof props.fillColor === 'string' ? props.fillColor : undefined)
    ?? (typeof attrs.fillColor === 'string' ? attrs.fillColor : undefined);
  const accentColor =
    (typeof attrs.accentColor === 'string' ? attrs.accentColor : undefined)
    ?? (typeof props.accentColor === 'string' ? props.accentColor : undefined)
    ?? (typeof node.strokeColor === 'string' ? node.strokeColor : undefined)
    ?? (typeof attrs.strokeColor === 'string' ? attrs.strokeColor : undefined)
    ?? (typeof attrs.borderColor === 'string' ? attrs.borderColor : undefined);
  const strokeColor =
    (typeof node.strokeColor === 'string' ? node.strokeColor : undefined)
    ?? (typeof props.strokeColor === 'string' ? props.strokeColor : undefined)
    ?? (typeof attrs.strokeColor === 'string' ? attrs.strokeColor : undefined)
    ?? (typeof attrs.borderColor === 'string' ? attrs.borderColor : undefined)
    ?? accentColor;

  // Export renders through a standalone SVG assembler, not the canvas's
  // NodesRenderer/useViewRules() — so a node kept only for reporting-chain
  // connectivity never gets the editor/viewer's dynamic opacity dim. The SVG
  // pipeline also has no per-shape opacity concept (only frames support one),
  // so the neutral treatment has to be baked in as flat colors here, sourced
  // from the same constants the editor/viewer use.
  const isChainContext = isReportingChainContextNode(node);
  const effectiveFillColor = isChainContext ? CHAIN_BACKGROUND_COLOR : fillColor;
  const effectiveStrokeColor = isChainContext ? CHAIN_BORDER_COLOR : strokeColor;
  const effectiveAccentColor = isChainContext ? CHAIN_BORDER_COLOR : accentColor;

  if (typeof name === 'string') {
    props.name = name;
    if (typeof props.label !== 'string') props.label = name;
    if (typeof props.text !== 'string') props.text = name;
  }
  if (typeof role === 'string' && typeof props.role !== 'string') props.role = role;
  if (typeof effectiveFillColor === 'string') {
    props.fillColor = effectiveFillColor;
    if (isChainContext || typeof attrs.fillColor !== 'string') attrs.fillColor = effectiveFillColor;
  }
  if (typeof effectiveStrokeColor === 'string') {
    props.strokeColor = effectiveStrokeColor;
    if (isChainContext || typeof attrs.strokeColor !== 'string') attrs.strokeColor = effectiveStrokeColor;
    if (isChainContext || typeof attrs.borderColor !== 'string') attrs.borderColor = effectiveStrokeColor;
  }
  if (typeof effectiveAccentColor === 'string') {
    props.accentColor = effectiveAccentColor;
    if (isChainContext || typeof attrs.accentColor !== 'string') attrs.accentColor = effectiveAccentColor;
  }

  return {
    id: node.id,
    type: node.type,
    version: node.version,
    category: 'orgchart',
    x: node.x,
    y: node.y,
    width,
    height,
    w: node.w ?? width,
    h: node.h ?? height,
    name,
    text: typeof node.text === 'string' ? node.text : name,
    locked: node.locked === true,
    lockOwner: node.lockOwner,
    ...(typeof effectiveFillColor === 'string' ? { fillColor: effectiveFillColor } : {}),
    strokeColor: effectiveStrokeColor,
    zIndex: node.zIndex,
    attrs,
    props,
    visible: true,
  };
};

const connectorRecordFromEdge = (edge: Edge): Record<string, unknown> => {
  const attrs = { ...(edge.attrs ?? {}) };
  return {
    id: edge.id,
    type: edge.type,
    from: edge.from,
    to: edge.to,
    sourceShapeId: edge.from,
    targetShapeId: edge.to,
    attrs,
    props: {},
    visible: true,
  };
};
